import { useState, useEffect } from 'react';
import { getAmadeusClientId, getAmadeusClientSecret } from '@/lib/api-keys';

const AMADEUS_API_URL = 'https://api.amadeus.com';

// Interfaces para a resposta da API
interface AmadeusError {
  code: string;
  title: string;
  detail: string;
  status: number;
}

interface AmadeusResponse<T> {
  data?: T[];
  meta?: {
    count: number;
    links?: {
      self: string;
      next?: string;
      previous?: string;
    };
  };
  errors?: AmadeusError[];
}

// Interface para sugestões de hotéis
interface HotelSuggestion {
  type: string;
  hotelId: string;
  name: string;
  iataCode?: string;
  address?: {
    cityName: string;
    countryCode: string;
  };
}

// Interface para informações de companhias aéreas
export interface AirlineInfo {
  type: string;
  iataCode: string;
  icaoCode: string;
  businessName: string;
  commonName: string;
}

// Interface para informações de voo
export interface FlightOffer {
  type: string;
  id: string;
  source: string;
  instantTicketingRequired: boolean;
  nonHomogeneous: boolean;
  oneWay: boolean;
  lastTicketingDate: string;
  numberOfBookableSeats: number;
  itineraries: Array<{
    duration: string;
    segments: Array<{
      departure: {
        iataCode: string;
        terminal?: string;
        at: string; // ISO 8601 date-time
      };
      arrival: {
        iataCode: string;
        terminal?: string;
        at: string; // ISO 8601 date-time
      };
      carrierCode: string;
      number: string;
      aircraft: {
        code: string;
      };
      operating?: {
        carrierCode: string;
      };
      duration: string;
      id: string;
      numberOfStops: number;
      blacklistedInEU: boolean;
    }>;
  }>;
  price: {
    currency: string;
    total: string;
    base: string;
    fees: Array<{
      amount: string;
      type: string;
    }>;
    grandTotal: string;
  };
  pricingOptions: {
    fareType: string[];
    includedCheckedBagsOnly: boolean;
  };
  validatingAirlineCodes: string[];
  travelerPricings: Array<{
    travelerId: string;
    fareOption: string;
    travelerType: string;
    price: {
      currency: string;
      total: string;
      base: string;
    };
    fareDetailsBySegment: Array<{
      segmentId: string;
      cabin: string;
      fareBasis: string;
      class: string;
      includedCheckedBags: {
        quantity: number;
      };
    }>;
  }>;
}

// Função para log detalhado de erros
const logApiError = (error: any, context: { flightNumber?: string; origin?: string; destination?: string; date?: string; }) => {
  console.error('Erro na API da Amadeus:', {
    error,
    context
  });
};

// Função para obter mensagem de erro formatada
const getErrorMessage = (error: any): string => {
  if (typeof error === 'string') {
    return error;
  }
  
  if (error?.errors && error.errors.length > 0) {
    return `Erro: ${error.errors[0].title} - ${error.errors[0].detail}`;
  }
  
  if (error?.message) {
    return `Erro: ${error.message}`;
  }
  
  return 'Ocorreu um erro desconhecido ao acessar a API da Amadeus';
};

export function useAmadeusApi() {
  const [isConfigured, setIsConfigured] = useState<boolean>(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [tokenExpiration, setTokenExpiration] = useState<number | null>(null);
  
  useEffect(() => {
    const clientId = getAmadeusClientId();
    const clientSecret = getAmadeusClientSecret();
    setIsConfigured(!!clientId && !!clientSecret && clientId.trim() !== '' && clientSecret.trim() !== '');
    
    // Verificar se já temos um token válido
    const storedToken = localStorage.getItem('amadeusAccessToken');
    const storedExpiration = localStorage.getItem('amadeusTokenExpiration');
    
    if (storedToken && storedExpiration) {
      const expirationTime = parseInt(storedExpiration, 10);
      // Se o token ainda é válido (com uma margem de segurança de 5 minutos)
      if (expirationTime > Date.now() + 5 * 60 * 1000) {
        setAccessToken(storedToken);
        setTokenExpiration(expirationTime);
      } else {
        // Token expirado, vamos removê-lo
        localStorage.removeItem('amadeusAccessToken');
        localStorage.removeItem('amadeusTokenExpiration');
      }
    }
  }, []);
  
  // Função para obter um token de acesso
  const getAccessToken = async (): Promise<string> => {
    // Se já temos um token válido, retorná-lo
    if (accessToken && tokenExpiration && tokenExpiration > Date.now() + 5 * 60 * 1000) {
      return accessToken;
    }
    
    const clientId = getAmadeusClientId();
    const clientSecret = getAmadeusClientSecret();
    
    if (!clientId || !clientSecret) {
      throw new Error('Credenciais da API da Amadeus não configuradas');
    }
    
    try {
      const response = await fetch('https://api.amadeus.com/v1/security/oauth2/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `grant_type=client_credentials&client_id=${clientId}&client_secret=${clientSecret}`
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw data;
      }
      
      // Calcular quando o token expira
      const expiresIn = data.expires_in || 1800; // Padrão: 30 minutos
      const expirationTime = Date.now() + expiresIn * 1000;
      
      // Salvar o token e sua expiração
      setAccessToken(data.access_token);
      setTokenExpiration(expirationTime);
      localStorage.setItem('amadeusAccessToken', data.access_token);
      localStorage.setItem('amadeusTokenExpiration', expirationTime.toString());
      
      return data.access_token;
    } catch (error) {
      logApiError(error, {});
      throw new Error(getErrorMessage(error));
    }
  };
  
  // Função para formatar o número do voo para o padrão esperado pela API
  const formatFlightNumber = (number: string): string => {
    if (!number) return '';
    
    // Remover espaços e converter para maiúsculas
    let formatted = number.replace(/\s+/g, '').toUpperCase();
    
    return formatted;
  };
  
  // Função para buscar informações de voo por número e data
  const searchFlightByNumber = async (flightNumber: string, date?: string): Promise<FlightOffer[] | null> => {
    console.log(`Buscando voo por número: ${flightNumber}, data: ${date}`);
    
    try {
      // Verificar se temos um token válido
      const token = await getAccessToken();
      if (!token) {
        console.error('Token não disponível para busca de voo');
        return null;
      }
      
      // Formatar o número do voo (remover espaços e converter para maiúsculas)
      const formattedFlightNumber = formatFlightNumber(flightNumber);
      
      // Extrair o código da companhia aérea e o número do voo
      const flightRegex = /^([A-Z0-9]{2})(\d{1,4})$/;
      const match = formattedFlightNumber.match(flightRegex);
      
      if (!match) {
        console.error('Formato de número de voo inválido. Use o formato XX1234 (código da companhia + número)');
        return null;
      }
      
      const [_, carrierCode, number] = match;
      console.log(`Código da companhia: ${carrierCode}, Número do voo: ${number}`);
      
      // Construir a URL para a API de Schedule Flights
      const url = new URL('https://api.amadeus.com/v2/schedule/flights');
      url.searchParams.append('carrierCode', carrierCode);
      url.searchParams.append('flightNumber', number);
      
      if (date) {
        url.searchParams.append('scheduledDepartureDate', date);
      } else {
        const today = new Date();
        const formattedDate = today.toISOString().split('T')[0];
        url.searchParams.append('scheduledDepartureDate', formattedDate);
      }
      
      console.log('URL da requisição:', url.toString());
      
      // Fazer a requisição para a API
      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      console.log('Resposta da API de Schedule Flights:', data);
      
      // Verificar se a resposta contém erros
      if (!response.ok || data.errors) {
        console.error('Erro na resposta da API:', data.errors);
        
        // Tentar a API de Flight Status como fallback
        console.log('Nenhum resultado encontrado na API de Schedule Flights. Tentando Flight Status API...');
        
        // Tentar buscar informações de voo usando a API de Flight Status
        try {
          const statusUrl = new URL('https://api.amadeus.com/v2/schedule/flights/statusByFlightNumber');
          statusUrl.searchParams.append('carrierCode', carrierCode);
          statusUrl.searchParams.append('flightNumber', number);
          
          if (date) {
            statusUrl.searchParams.append('scheduledDepartureDate', date);
          } else {
            const today = new Date();
            const formattedDate = today.toISOString().split('T')[0];
            statusUrl.searchParams.append('scheduledDepartureDate', formattedDate);
          }
          
          console.log('Tentando API de Flight Status:', statusUrl.toString());
          
          const statusResponse = await fetch(statusUrl.toString(), {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          const statusData = await statusResponse.json();
          console.log('Resposta da API de Flight Status:', statusData);
          
          if (!statusResponse.ok || statusData.errors) {
            console.error('Erro na resposta da API de Flight Status:', statusData.errors);
            // Se também falhar, retornar null
            console.log('Nenhum resultado encontrado em ambas as APIs.');
            return null;
          }
          
          // Processar os dados da API de Flight Status
          if (statusData.data && statusData.data.length > 0) {
            console.log('Processando dados da API de Flight Status...');
            
            // Transformar os dados da API de Flight Status em FlightOffer
            const flightOffers: FlightOffer[] = statusData.data.map((flight: any) => {
              try {
                console.log('Processando voo da API de Flight Status:', flight);
                
                // Verificar se temos os dados necessários
                if (!flight.flightPoints || flight.flightPoints.length < 2) {
                  console.error('Dados de pontos de voo incompletos:', flight);
                  return null;
                }
                
                // Encontrar o ponto de partida e chegada
                const departurePoint = flight.flightPoints.find((point: any) => point.departure);
                const arrivalPoint = flight.flightPoints.find((point: any) => point.arrival);
                
                if (!departurePoint || !arrivalPoint) {
                  console.error('Pontos de partida ou chegada não encontrados:', flight);
                  return null;
                }
                
                // Extrair horários de partida e chegada
                const departureTime = departurePoint.departure.timings?.find((timing: any) => 
                  timing.qualifier === 'STD' || timing.qualifier === 'ETD' || timing.qualifier === 'ATD'
                )?.value;
                
                const arrivalTime = arrivalPoint.arrival.timings?.find((timing: any) => 
                  timing.qualifier === 'STA' || timing.qualifier === 'ETA' || timing.qualifier === 'ATA'
                )?.value;
                
                if (!departureTime || !arrivalTime) {
                  console.error('Horários de partida ou chegada não encontrados:', flight);
                  return null;
                }
                
                console.log(`Horário de partida: ${departureTime}, Horário de chegada: ${arrivalTime}`);
                
                // Extrair duração do voo
                let duration = flight.segments?.[0]?.scheduledSegmentDuration || 
                               flight.legs?.[0]?.scheduledLegDuration || 'PT2H';
                
                console.log(`Duração do voo: ${duration}`);
                
                // Criar segmentos para o FlightOffer
                const segments = flight.segments?.map((segment: any, index: number) => {
                  const segmentDeparturePoint = flight.flightPoints.find((point: any) => 
                    point.iataCode === segment.boardPointIataCode && point.departure
                  );
                  
                  const segmentArrivalPoint = flight.flightPoints.find((point: any) => 
                    point.iataCode === segment.offPointIataCode && point.arrival
                  );
                  
                  const segmentDepartureTime = segmentDeparturePoint?.departure.timings?.find((timing: any) => 
                    timing.qualifier === 'STD' || timing.qualifier === 'ETD' || timing.qualifier === 'ATD'
                  )?.value || departureTime;
                  
                  const segmentArrivalTime = segmentArrivalPoint?.arrival.timings?.find((timing: any) => 
                    timing.qualifier === 'STA' || timing.qualifier === 'ETA' || timing.qualifier === 'ATA'
                  )?.value || arrivalTime;
                  
                  // Determinar o código da companhia e número do voo
                  // Para o código da companhia, usamos o código da companhia original (marketing)
                  const marketingCarrierCode = flight.flightDesignator.carrierCode;
                  // Para o número do voo, sempre usamos o número do voo original (marketing)
                  const flightNum = flight.flightDesignator.flightNumber.toString();
                  
                  return {
                    departure: {
                      iataCode: segment.boardPointIataCode,
                      at: segmentDepartureTime,
                      terminal: segmentDeparturePoint?.departure?.terminal?.code
                    },
                    arrival: {
                      iataCode: segment.offPointIataCode,
                      at: segmentArrivalTime,
                      terminal: segmentArrivalPoint?.arrival?.terminal?.code
                    },
                    carrierCode: marketingCarrierCode,
                    number: flightNum,
                    aircraft: {
                      code: flight.legs?.[index]?.aircraftEquipment?.aircraftType || 'UNK'
                    },
                    operating: {
                      carrierCode: segment.partnership?.operatingFlight?.carrierCode || flight.flightDesignator.carrierCode
                    },
                    duration: segment.scheduledSegmentDuration || flight.legs?.[index]?.scheduledLegDuration || 'PT2H',
                    id: `${index + 1}`,
                    numberOfStops: 0,
                    blacklistedInEU: false
                  };
                }) || [];
                
                // Se não temos segmentos, criar um segmento padrão
                if (segments.length === 0) {
                  const marketingCarrierCode = flight.flightDesignator.carrierCode;
                  const flightNum = flight.flightDesignator.flightNumber.toString();
                  
                  segments.push({
                    departure: {
                      iataCode: departurePoint.iataCode,
                      at: departureTime,
                      terminal: departurePoint.departure?.terminal?.code
                    },
                    arrival: {
                      iataCode: arrivalPoint.iataCode,
                      at: arrivalTime,
                      terminal: arrivalPoint.arrival?.terminal?.code
                    },
                    carrierCode: marketingCarrierCode,
                    number: flightNum,
                    aircraft: {
                      code: flight.legs?.[0]?.aircraftEquipment?.aircraftType || 'UNK'
                    },
                    operating: {
                      carrierCode: flight.legs?.[0]?.aircraftEquipment ? marketingCarrierCode : marketingCarrierCode
                    },
                    duration: duration,
                    id: '1',
                    numberOfStops: 0,
                    blacklistedInEU: false
                  });
                }
                
                console.log(`Número de segmentos: ${segments.length}`);
                
                // Criar o FlightOffer
                return {
                  type: 'flight-offer',
                  id: `${flight.flightDesignator.carrierCode}${flight.flightDesignator.flightNumber}-${Date.now()}`,
                  source: 'GDS',
                  instantTicketingRequired: false,
                  nonHomogeneous: false,
                  oneWay: true,
                  lastTicketingDate: flight.scheduledDepartureDate,
                  numberOfBookableSeats: 1,
                  itineraries: [
                    {
                      duration: duration,
                      segments: segments
                    }
                  ],
                  price: {
                    currency: 'USD',
                    total: '0',
                    base: '0',
                    fees: [],
                    grandTotal: '0'
                  },
                  pricingOptions: {
                    fareType: ['PUBLISHED'],
                    includedCheckedBagsOnly: false
                  },
                  validatingAirlineCodes: [flight.flightDesignator.carrierCode],
                  travelerPricings: [
                    {
                      travelerId: '1',
                      fareOption: 'STANDARD',
                      travelerType: 'ADULT',
                      price: {
                        currency: 'USD',
                        total: '0',
                        base: '0'
                      },
                      fareDetailsBySegment: segments.map((segment: any, index: number) => ({
                        segmentId: `${index + 1}`,
                        cabin: 'ECONOMY',
                        fareBasis: 'UNKNOWN',
                        class: 'UNKNOWN',
                        includedCheckedBags: {
                          quantity: 0
                        }
                      }))
                    }
                  ]
                };
              } catch (e) {
                console.error('Erro ao processar dados de voo:', e);
                return null;
              }
            }).filter(Boolean) as FlightOffer[];
            
            if (flightOffers.length > 0) {
              console.log(`${flightOffers.length} voos encontrados na API de Flight Status.`);
              return flightOffers;
            } else {
              console.log('Nenhum voo válido encontrado na API de Flight Status.');
              return null;
            }
          } else {
            console.log('Nenhum resultado encontrado na API de Flight Status.');
            return null;
          }
        } catch (e) {
          console.error('Erro ao buscar dados na API de Flight Status:', e);
          return null;
        }
      }
      
      // Processar os dados da API de Schedule Flights
      if (data.data && data.data.length > 0) {
        console.log('Processando dados da API de Schedule Flights...');
        
        // Transformar os dados da API de Schedule Flights em FlightOffer
        const flightOffers: FlightOffer[] = data.data.map((flight: any) => {
          try {
            console.log('Processando voo da API de Schedule Flights:', flight);
            
            // Verificar se temos os dados necessários
            if (!flight.flightPoints || flight.flightPoints.length < 2) {
              console.error('Dados de pontos de voo incompletos:', flight);
              return null;
            }
            
            // Encontrar o ponto de partida e chegada
            const departurePoint = flight.flightPoints.find((point: any) => point.departure);
            const arrivalPoint = flight.flightPoints.find((point: any) => point.arrival);
            
            if (!departurePoint || !arrivalPoint) {
              console.error('Pontos de partida ou chegada não encontrados:', flight);
              return null;
            }
            
            // Extrair horários de partida e chegada
            const departureTime = departurePoint.departure.timings?.find((timing: any) => 
              timing.qualifier === 'STD' || timing.qualifier === 'ETD' || timing.qualifier === 'ATD'
            )?.value;
            
            const arrivalTime = arrivalPoint.arrival.timings?.find((timing: any) => 
              timing.qualifier === 'STA' || timing.qualifier === 'ETA' || timing.qualifier === 'ATA'
            )?.value;
            
            if (!departureTime || !arrivalTime) {
              console.error('Horários de partida ou chegada não encontrados:', flight);
              return null;
            }
            
            console.log(`Horário de partida: ${departureTime}, Horário de chegada: ${arrivalTime}`);
            
            // Extrair duração do voo
            let duration = flight.segments?.[0]?.scheduledSegmentDuration || 
                           flight.legs?.[0]?.scheduledLegDuration || 'PT2H';
            
            console.log(`Duração do voo: ${duration}`);
            
            // Criar segmentos para o FlightOffer
            const segments = flight.segments?.map((segment: any, index: number) => {
              const segmentDeparturePoint = flight.flightPoints.find((point: any) => 
                point.iataCode === segment.boardPointIataCode && point.departure
              );
              
              const segmentArrivalPoint = flight.flightPoints.find((point: any) => 
                point.iataCode === segment.offPointIataCode && point.arrival
              );
              
              const segmentDepartureTime = segmentDeparturePoint?.departure.timings?.find((timing: any) => 
                timing.qualifier === 'STD' || timing.qualifier === 'ETD' || timing.qualifier === 'ATD'
              )?.value || departureTime;
              
              const segmentArrivalTime = segmentArrivalPoint?.arrival.timings?.find((timing: any) => 
                timing.qualifier === 'STA' || timing.qualifier === 'ETA' || timing.qualifier === 'ATA'
              )?.value || arrivalTime;
              
              // Determinar o código da companhia e número do voo
              // Para o código da companhia, usamos o código da companhia original (marketing)
              const marketingCarrierCode = flight.flightDesignator.carrierCode;
              // Para o número do voo, sempre usamos o número do voo original (marketing)
              const flightNum = flight.flightDesignator.flightNumber.toString();
              
              return {
                departure: {
                  iataCode: segment.boardPointIataCode,
                  at: segmentDepartureTime,
                  terminal: segmentDeparturePoint?.departure?.terminal?.code
                },
                arrival: {
                  iataCode: segment.offPointIataCode,
                  at: segmentArrivalTime,
                  terminal: segmentArrivalPoint?.arrival?.terminal?.code
                },
                carrierCode: marketingCarrierCode,
                number: flightNum,
                aircraft: {
                  code: flight.legs?.[index]?.aircraftEquipment?.aircraftType || 'UNK'
                },
                operating: {
                  carrierCode: segment.partnership?.operatingFlight?.carrierCode || flight.flightDesignator.carrierCode
                },
                duration: segment.scheduledSegmentDuration || flight.legs?.[index]?.scheduledLegDuration || 'PT2H',
                id: `${index + 1}`,
                numberOfStops: 0,
                blacklistedInEU: false
              };
            }) || [];
            
            // Se não temos segmentos, criar um segmento padrão
            if (segments.length === 0) {
              const marketingCarrierCode = flight.flightDesignator.carrierCode;
              const flightNum = flight.flightDesignator.flightNumber.toString();
              
              segments.push({
                departure: {
                  iataCode: departurePoint.iataCode,
                  at: departureTime,
                  terminal: departurePoint.departure?.terminal?.code
                },
                arrival: {
                  iataCode: arrivalPoint.iataCode,
                  at: arrivalTime,
                  terminal: arrivalPoint.arrival?.terminal?.code
                },
                carrierCode: marketingCarrierCode,
                number: flightNum,
                aircraft: {
                  code: flight.legs?.[0]?.aircraftEquipment?.aircraftType || 'UNK'
                },
                operating: {
                  carrierCode: flight.legs?.[0]?.aircraftEquipment ? marketingCarrierCode : marketingCarrierCode
                },
                duration: duration,
                id: '1',
                numberOfStops: 0,
                blacklistedInEU: false
              });
            }
            
            console.log(`Número de segmentos: ${segments.length}`);
            
            // Criar o FlightOffer
            return {
              type: 'flight-offer',
              id: `${flight.flightDesignator.carrierCode}${flight.flightDesignator.flightNumber}-${Date.now()}`,
              source: 'GDS',
              instantTicketingRequired: false,
              nonHomogeneous: false,
              oneWay: true,
              lastTicketingDate: flight.scheduledDepartureDate,
              numberOfBookableSeats: 1,
              itineraries: [
                {
                  duration: duration,
                  segments: segments
                }
              ],
              price: {
                currency: 'USD',
                total: '0',
                base: '0',
                fees: [],
                grandTotal: '0'
              },
              pricingOptions: {
                fareType: ['PUBLISHED'],
                includedCheckedBagsOnly: false
              },
              validatingAirlineCodes: [flight.flightDesignator.carrierCode],
              travelerPricings: [
                {
                  travelerId: '1',
                  fareOption: 'STANDARD',
                  travelerType: 'ADULT',
                  price: {
                    currency: 'USD',
                    total: '0',
                    base: '0'
                  },
                  fareDetailsBySegment: segments.map((segment: any, index: number) => ({
                    segmentId: `${index + 1}`,
                    cabin: 'ECONOMY',
                    fareBasis: 'UNKNOWN',
                    class: 'UNKNOWN',
                    includedCheckedBags: {
                      quantity: 0
                    }
                  }))
                }
              ]
            };
          } catch (e) {
            console.error('Erro ao processar dados de voo:', e);
            return null;
          }
        }).filter(Boolean) as FlightOffer[];
        
        if (flightOffers.length > 0) {
          console.log(`${flightOffers.length} voos encontrados na API de Schedule Flights.`);
          return flightOffers;
        } else {
          console.log('Nenhum voo válido encontrado na API de Schedule Flights.');
          return null;
        }
      } else {
        console.log('Nenhum resultado encontrado na API de Schedule Flights.');
        return null;
      }
    } catch (e) {
      console.error('Erro ao buscar voo por número:', e);
      return null;
    }
  };
  
  // Função para buscar ofertas de voo
  const searchFlightOffers = async (
    carrierCode?: string,
    flightNumber?: string,
    date?: string,
    origin?: string,
    destination?: string
  ): Promise<FlightOffer[]> => {
    try {
      // Obter token de acesso
      const token = await getAccessToken();
      
      // Se não temos origem e destino, mas temos código da companhia e número do voo,
      // não podemos fazer a busca de ofertas
      if (!origin || !destination) {
        if (carrierCode && flightNumber) {
          throw new Error('Não foi possível encontrar informações para este voo. Por favor, tente especificar a origem e o destino.');
        } else {
          throw new Error('Origem e destino são obrigatórios para buscar ofertas de voo.');
        }
      }
      
      // Construir URL para a busca
      const searchUrl = new URL('https://api.amadeus.com/v2/shopping/flight-offers');
      searchUrl.searchParams.append('originLocationCode', origin);
      searchUrl.searchParams.append('destinationLocationCode', destination);
      searchUrl.searchParams.append('departureDate', date || new Date().toISOString().split('T')[0]);
      searchUrl.searchParams.append('adults', '1');
      searchUrl.searchParams.append('max', '100');
      
      // Adicionar filtro de companhia aérea se fornecido
      if (carrierCode) {
        searchUrl.searchParams.append('includedAirlineCodes', carrierCode);
      }
      
      // Fazer a requisição
      const response = await fetch(searchUrl.toString(), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data: AmadeusResponse<FlightOffer> = await response.json();
      
      // Verificar se houve erro
      if (!response.ok || data.errors) {
        throw data;
      }
      
      // Filtrar por número de voo se fornecido
      let offers = data.data || [];
      
      if (flightNumber && carrierCode) {
        offers = offers.filter(offer => {
          return offer.itineraries.some(itinerary => 
            itinerary.segments.some(segment => 
              segment.carrierCode === carrierCode && segment.number === flightNumber
            )
          );
        });
      }
      
      return offers;
    } catch (error) {
      logApiError(error, { flightNumber: carrierCode && flightNumber ? `${carrierCode}${flightNumber}` : undefined, origin, destination, date });
      throw new Error(getErrorMessage(error));
    }
  };
  
  // Função para testar a conexão com a API
  const testConnection = async (): Promise<boolean> => {
    try {
      // Tentar obter um token de acesso
      const token = await getAccessToken();
      
      // Testar o endpoint de Hotel Name Autocomplete com uma busca simples
      console.log('Testando endpoint de Hotel Name Autocomplete...');
      const testResponse = await fetch(`${AMADEUS_API_URL}/v1/reference-data/locations/hotels/by-keyword?keyword=Hilton`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      
      if (!testResponse.ok) {
        console.error(`Erro ao testar endpoint de Hotel Name Autocomplete: ${testResponse.status}`);
        const errorData = await testResponse.json();
        console.error('Detalhes do erro:', errorData);
        
        // Verificar se é um erro de CORS
        if (testResponse.status === 0 || testResponse.type === 'opaque') {
          console.error('Possível erro de CORS. Verifique se a API permite requisições do seu domínio.');
        }
        
        return false;
      }
      
      console.log('Conexão com a API Amadeus estabelecida com sucesso!');
      return true;
    } catch (error) {
      console.error('Erro ao testar conexão com a API Amadeus:', error);
      return false;
    }
  };
  
  // Função para buscar informações de companhias aéreas
  const getAirlineInfo = async (airlineCode: string): Promise<AirlineInfo | null> => {
    if (!isConfigured) {
      console.error('API da Amadeus não configurada');
      return null;
    }

    try {
      // Obter token de acesso
      const token = await getAccessToken();
      if (!token) {
        console.error('Não foi possível obter o token de acesso');
        return null;
      }

      // Construir URL para a API de busca de companhias aéreas
      const apiUrl = new URL('https://api.amadeus.com/v1/reference-data/airlines');
      apiUrl.searchParams.append('airlineCodes', airlineCode);

      console.log(`Buscando informações da companhia aérea: ${airlineCode}`);
      console.log(`URL da API: ${apiUrl.toString()}`);

      // Fazer a requisição
      const response = await fetch(apiUrl.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Erro ao buscar informações da companhia aérea:', errorData);
        return null;
      }

      const data: AmadeusResponse<AirlineInfo> = await response.json();
      console.log('Resposta da API de companhias aéreas:', data);

      if (!data.data || data.data.length === 0) {
        console.log(`Nenhuma informação encontrada para a companhia aérea ${airlineCode}`);
        return null;
      }

      return data.data[0];
    } catch (error) {
      console.error('Erro ao buscar informações da companhia aérea:', error);
      return null;
    }
  };

  const searchHotels = async (cityCode: string) => {
    try {
      const token = await getAccessToken();
      
      const response = await fetch(`${AMADEUS_API_URL}/v1/reference-data/locations/hotels/by-city?cityCode=${cityCode}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Error searching hotels:', error);
      throw error;
    }
  };

  const searchHotelsByName = async (keyword: string) => {
    if (!keyword || keyword.length < 3) {
      return [];
    }

    try {
      const token = await getAccessToken();
      
      // Endpoint correto conforme a especificação Swagger
      console.log(`Buscando hotéis com o termo: "${keyword}"`);
      
      // O endpoint correto é /reference-data/locations/hotel (sem 's' no final)
      const response = await fetch(`${AMADEUS_API_URL}/v1/reference-data/locations/hotel?keyword=${encodeURIComponent(keyword)}&subType=HOTEL_LEISURE&subType=HOTEL_GDS`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
        
        // Se falhar, tentar com dados simulados para demonstração
        console.log('Retornando dados simulados para demonstração');
        return [
          {
            type: 'hotel',
            hotelId: `MOCK_${Date.now()}`,
            name: `${keyword} (Simulado)`,
            address: {
              cityName: 'Rio de Janeiro',
              countryCode: 'BR'
            }
          }
        ];
      }
      
      const data = await response.json();
      console.log('Resposta da API de busca de hotéis:', data);
      
      if (!data.data || !Array.isArray(data.data) || data.data.length === 0) {
        console.log('Nenhum hotel encontrado, retornando dados simulados');
        return [
          {
            type: 'hotel',
            hotelId: `MOCK_${Date.now()}`,
            name: `${keyword} (Simulado)`,
            address: {
              cityName: 'Rio de Janeiro',
              countryCode: 'BR'
            }
          }
        ];
      }
      
      // Mapear os resultados para o formato esperado pela aplicação
      // A API retorna hotelIds como array, mas nossa aplicação espera um único hotelId
      return data.data.map((hotel: {
        type?: string;
        hotelIds?: string[];
        name: string;
        iataCode?: string;
        address?: {
          cityName: string;
          countryCode: string;
        };
      }) => ({
        type: hotel.type || 'hotel',
        hotelId: hotel.hotelIds && hotel.hotelIds.length > 0 ? hotel.hotelIds[0] : `MOCK_${Date.now()}`,
        name: hotel.name,
        iataCode: hotel.iataCode,
        address: hotel.address
      }));
    } catch (error) {
      console.error('Error searching hotels by name:', error);
      
      // Retornar dados simulados em caso de erro para demonstração
      return [
        {
          type: 'hotel',
          hotelId: `MOCK_${Date.now()}`,
          name: `${keyword} (Simulado)`,
          address: {
            cityName: 'Rio de Janeiro',
            countryCode: 'BR'
          }
        }
      ];
    }
  };

  const getHotelOffers = async (hotelId: string) => {
    try {
      const token = await getAccessToken();
      
      // Endpoint correto para Hotel Search
      console.log(`Buscando ofertas para o hotel ID: ${hotelId}`);
      
      // Se for um hotel simulado, retornar dados fictícios
      if (hotelId.startsWith('MOCK_')) {
        console.log('Retornando dados simulados para o hotel');
        return {
          hotel: {
            hotelId: hotelId,
            name: hotelId.replace('MOCK_', '').replace(Date.now().toString(), '') + ' (Simulado)',
            rating: '4',
            description: {
              text: 'Este é um hotel simulado para fins de demonstração. Os dados não são reais.',
              lang: 'pt-BR'
            },
            amenities: ['Wi-Fi', 'Piscina', 'Academia', 'Restaurante', 'Bar'],
            address: {
              lines: ['Av. Exemplo, 123'],
              cityName: 'Rio de Janeiro',
              countryCode: 'BR',
              postalCode: '22000-000'
            }
          },
          available: true,
          offers: [
            {
              id: `offer_${Date.now()}_1`,
              checkInDate: new Date().toISOString().split('T')[0],
              checkOutDate: new Date(Date.now() + 86400000 * 3).toISOString().split('T')[0],
              roomType: 'Quarto Standard',
              price: {
                currency: 'BRL',
                total: '450.00'
              },
              policies: {
                cancellation: {
                  description: 'Cancelamento gratuito até 24 horas antes do check-in'
                }
              }
            },
            {
              id: `offer_${Date.now()}_2`,
              checkInDate: new Date().toISOString().split('T')[0],
              checkOutDate: new Date(Date.now() + 86400000 * 3).toISOString().split('T')[0],
              roomType: 'Quarto Luxo',
              price: {
                currency: 'BRL',
                total: '650.00'
              },
              policies: {
                cancellation: {
                  description: 'Cancelamento gratuito até 48 horas antes do check-in'
                }
              }
            }
          ]
        };
      }
      
      // Corrigindo a URL para evitar duplicação de "v1"
      const response = await fetch(`${AMADEUS_API_URL}/v2/shopping/hotel-offers/by-hotel?hotelId=${hotelId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
        
        // Retornar dados simulados se a API falhar
        return {
          hotel: {
            hotelId: hotelId,
            name: 'Hotel (Dados Simulados)',
            rating: '3',
            description: {
              text: 'Não foi possível obter dados reais deste hotel. Estes são dados simulados.',
              lang: 'pt-BR'
            },
            amenities: ['Wi-Fi', 'Estacionamento'],
            address: {
              lines: ['Endereço não disponível'],
              cityName: 'Cidade',
              countryCode: 'BR'
            }
          },
          available: true,
          offers: [
            {
              id: `offer_${Date.now()}`,
              checkInDate: new Date().toISOString().split('T')[0],
              checkOutDate: new Date(Date.now() + 86400000 * 2).toISOString().split('T')[0],
              roomType: 'Quarto Standard',
              price: {
                currency: 'BRL',
                total: '350.00'
              }
            }
          ]
        };
      }
      
      const data = await response.json();
      console.log('Resposta da API de ofertas de hotel:', data);
      
      if (!data.data || data.data.length === 0) {
        console.log('Nenhuma oferta encontrada, retornando dados simulados');
        return {
          hotel: {
            hotelId: hotelId,
            name: 'Hotel (Sem Ofertas)',
            description: {
              text: 'Não há ofertas disponíveis para este hotel no momento.',
              lang: 'pt-BR'
            }
          },
          available: false,
          offers: []
        };
      }
      
      return data.data[0];
    } catch (error) {
      console.error('Error getting hotel offers:', error);
      
      // Retornar dados simulados em caso de erro
      return {
        hotel: {
          hotelId: hotelId,
          name: 'Hotel (Erro)',
          description: {
            text: 'Ocorreu um erro ao buscar informações deste hotel.',
            lang: 'pt-BR'
          }
        },
        available: false,
        offers: []
      };
    }
  };

  // Busca ofertas para múltiplos hotéis (até 20 hotelIds)
  const getHotelOffersBatch = async (
    hotelIds: string[],
    checkInDate?: string,
    checkOutDate?: string,
    adults?: number,
    children?: number
  ) => {
    if (!hotelIds || hotelIds.length === 0) return [];
    const token = await getAccessToken();
    const params = new URLSearchParams();
    params.append('hotelIds', hotelIds.join(','));
    if (checkInDate) params.append('checkInDate', checkInDate);
    if (checkOutDate) params.append('checkOutDate', checkOutDate);
    if (adults) params.append('adults', adults.toString());
    if (children) params.append('childAges', Array(children).fill(8).join(',')); // Exemplo: todas as crianças com 8 anos
    const url = `https://api.amadeus.com/v3/shopping/hotel-offers?${params.toString()}`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      // data.data é um array de hotel-offers
      return data.data || [];
    } catch (error) {
      console.error('Erro ao buscar ofertas em lote:', error);
      return [];
    }
  };

  return {
    isConfigured,
    searchFlightByNumber,
    searchFlightOffers,
    testConnection,
    getAirlineInfo,
    searchHotels,
    searchHotelsByName,
    getHotelOffers,
    getHotelOffersBatch,
  };
} 